import { useState, useEffect } from "react";
import { db } from "../../../firebase/firestore";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc
} from "firebase/firestore";

import { CLOUD_NAME, UPLOAD_PRESET } from "../../../utils/cloudinary";

export default function Items() {
  const [items, setItems] = useState([]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "snacks",
    stockGrams: "",
    image: "",
    price250: "",
    price500: "",
    price1000: ""
  });

  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");

  const MAX_IMAGE_SIZE = 3 * 1024 * 1024;
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

  // 🔥 FETCH
  const fetchItems = async () => {
    const snap = await getDocs(collection(db, "items"));
    setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // 🔥 IMAGE UPLOAD
  const uploadImage = async (file) => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("Only JPG, PNG or WEBP files are allowed");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      alert("Image must be smaller than 3MB");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);

    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
      {
        method: "POST",
        body: formData
      }
    );

    const data = await res.json();

    if (!res.ok) {
      alert(data.error?.message || "Upload failed");
      setUploading(false);
      return;
    }

    setForm(prev => ({ ...prev, image: data.secure_url }));
    setUploading(false);
  };

  // 🔥 ADD / UPDATE
  const handleSubmit = async () => {
    if (!form.name || !form.image || !form.stockGrams) {
      return alert("Fill all fields");
    }

    if (Number(form.stockGrams) < 0) {
      return alert("Stock cannot be negative");
    }

    if ([form.price250, form.price500, form.price1000].some((p) => Number(p || 0) < 0)) {
      return alert("Prices cannot be negative");
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      image: form.image,
      stockGrams: Number(form.stockGrams),

      prices: {
        "250": Number(form.price250 || 0),
        "500": Number(form.price500 || 0),
        "1000": Number(form.price1000 || 0)
      }
    };

    if (editingId) {
      await updateDoc(doc(db, "items", editingId), payload);
    } else {
      await addDoc(collection(db, "items"), {
        ...payload,
        createdAt: new Date()
      });
    }

    resetForm();
    fetchItems();
  };

  // 🔥 DELETE
  const handleDelete = async (id) => {
    if (!window.confirm("Delete item?")) return;
    await deleteDoc(doc(db, "items", id));
    fetchItems();
  };

  // 🔥 EDIT
  const handleEdit = (item) => {
    setForm({
      ...item,
      price250: item.prices?.["250"] || "",
      price500: item.prices?.["500"] || "",
      price1000: item.prices?.["1000"] || ""
    });
    setEditingId(item.id);
  };

  // 🔥 RESET
  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      category: "snacks",
      stockGrams: "",
      image: "",
      price250: "",
      price500: "",
      price1000: ""
    });
    setEditingId(null);
  };

  const filtered = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="items-page">

      <h1>🍽 Manage Items (Multi Pricing)</h1>

      {/* 🔍 SEARCH */}
      <input
        className="search"
        placeholder="Search items..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* 🔥 FORM */}
      <div className="card form-card">

        <h3>{editingId ? "Edit Item" : "Add Item"}</h3>

        <div className="form-grid">

          <input
            placeholder="Item Name"
            value={form.name}
            onChange={(e)=>setForm({...form,name:e.target.value})}
          />

          <input
            placeholder="Stock (grams)"
            value={form.stockGrams}
            onChange={(e)=>setForm({...form,stockGrams:e.target.value})}
          />

          <select
            value={form.category}
            onChange={(e)=>setForm({...form,category:e.target.value})}
          >
            <option value="snacks">Snacks</option>
            <option value="sweets">Sweets</option>
            <option value="combo">Combo</option>
          </select>

        </div>

        {/* 💰 MULTI PRICES */}
        <div className="price-grid">

          <input
            placeholder="Price (250g)"
            value={form.price250}
            onChange={(e)=>setForm({...form,price250:e.target.value})}
          />

          <input
            placeholder="Price (500g)"
            value={form.price500}
            onChange={(e)=>setForm({...form,price500:e.target.value})}
          />

          <input
            placeholder="Price (1kg)"
            value={form.price1000}
            onChange={(e)=>setForm({...form,price1000:e.target.value})}
          />

        </div>

        <textarea
          placeholder="Description"
          value={form.description}
          onChange={(e)=>setForm({...form,description:e.target.value})}
        />

        {/* IMAGE */}
        <div className="upload-box">
          {uploading ? "Uploading..." : "Upload Image"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e)=>uploadImage(e.target.files[0])}
          />
        </div>

        {form.image && <img src={form.image} className="preview" alt="preview" />}

        <button onClick={handleSubmit}>
          {editingId ? "Update Item" : "Add Item"}
        </button>

      </div>

      {/* 🔥 TABLE */}
      <div className="table-container">

        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Prices</th>
              <th>Stock</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map(item => (
              <tr key={item.id}>

                <td>
                  <img src={item.image} className="table-img" alt={item.name}/>
                </td>

                <td>{item.name}</td>

                <td>
                  250g: ₹{item.prices?.["250"]} <br/>
                  500g: ₹{item.prices?.["500"]} <br/>
                  1kg: ₹{item.prices?.["1000"]}
                </td>

                <td>
                  {item.stockGrams}g <br/>
                  <small>{(item.stockGrams/1000).toFixed(2)} kg</small>
                </td>

                <td>
                  <button onClick={()=>handleEdit(item)}>Edit</button>
                  <button onClick={()=>handleDelete(item.id)}>Delete</button>
                </td>

              </tr>
            ))}
          </tbody>

        </table>

      </div>

    </div>
  );
}